import React from 'react'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Link } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface WebhookEvent {
  id: number
  webhook_id: number
  webhook_name: string
  status: string
  status_color: string
  status_label: string
  payload: Record<string, unknown>
  headers: Record<string, string>
  query_params: Record<string, string>
  ip_address: string | null
  content_type: string | null
  request_method: string
  processed_at: string | null
  processed_by_agent_id: number | null
  created_at: string
  updated_at: string
}

interface Props {
  event: WebhookEvent
}

export default function Show({ event }: Props) {
  return (
    <SidebarLayout
      section="monitoring"
      title={`Event #${event.id}`}
      action={
        <Button component={Link} href="/monitoring/webhook_events" variant="outlined">
          Back to events
        </Button>
      }
    >
      <Box sx={{ maxWidth: 900 }}>
        {/* Metadata */}
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
          <Typography variant="h3" sx={{ mb: 2 }}>Event Details</Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, width: 180 }}>Status</TableCell>
                  <TableCell>
                    <Chip
                      label={event.status_label}
                      size="small"
                      sx={{ backgroundColor: event.status_color, color: '#fff' }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Webhook</TableCell>
                  <TableCell>{event.webhook_name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Request Method</TableCell>
                  <TableCell>{event.request_method}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Content-Type</TableCell>
                  <TableCell>{event.content_type || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>IP Address</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{event.ip_address || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Received</TableCell>
                  <TableCell>{new Date(event.created_at).toLocaleString()}</TableCell>
                </TableRow>
                {event.processed_at && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500 }}>Processed</TableCell>
                    <TableCell>{new Date(event.processed_at).toLocaleString()}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Payload */}
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
          <Typography variant="h3" sx={{ mb: 2 }}>Payload</Typography>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'grey.50',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.8rem',
              fontFamily: 'monospace',
              maxHeight: 400,
              border: '1px solid',
              borderColor: 'divider',
              m: 0,
            }}
          >
            {JSON.stringify(event.payload, null, 2)}
          </Box>
        </Paper>

        {/* Headers */}
        {Object.keys(event.headers).length > 0 && (
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
            <Typography variant="h3" sx={{ mb: 2 }}>Headers</Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  {Object.entries(event.headers).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ fontWeight: 500, width: 250, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {key}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {value}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Query Params */}
        {Object.keys(event.query_params).length > 0 && (
          <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
            <Typography variant="h3" sx={{ mb: 2 }}>Query Parameters</Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  {Object.entries(event.query_params).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ fontWeight: 500, width: 250, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {key}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {value}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </SidebarLayout>
  )
}
